<template>
  <div class="pa-4">
    
    <v-btn text small class="mb-3" @click="$router.back()">
      <v-icon left small>mdi-arrow-left</v-icon>
      Volver
    </v-btn>

    <v-row v-if="loadingDetail" class="fill-height" align-content="center" justify="center">
      <v-col cols="6">
        <v-col
          class="text-subtitle-1 text-center"
          cols="12"
        >
          Consultando detalle del evento por favor espere...
        </v-col>
        <v-progress-linear color="red darken-4" indeterminate rounded height="6" />
      </v-col>
    </v-row>

    <v-alert v-if="error && !loadingDetail" type="error" dismissible>
      {{ error }}
    </v-alert>

    <v-alert
      v-if="copyAlert"
      text
      type="info"
      dense
      class="my-2"
    >
      {{ copyAlertMessage }}
    </v-alert>

    <template v-if="eventDetail && !loadingDetail">
      
      <base-card class="mb-4">
        <v-card-title class="d-flex align-center">
          <v-chip
            :color="getStatusColor(eventDetail.final_status)"
            dark
            label
            class="mr-3"
          >
            <v-icon left small>{{ getStatusIcon(eventDetail.final_status) }}</v-icon>
            {{ eventDetail.final_status }}
          </v-chip>
          <span class="subtitle-1 font-weight-bold">
            {{ eventDetail.prefix }}-{{ eventDetail.consecutive }}
          </span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">Trace ID</div>
              <div class="font-weight-medium text-truncate">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">{{ eventDetail.trace_id }}</span>
                  </template>
                  {{ eventDetail.trace_id }}
                </v-tooltip>
                <v-icon
                  @click="copyToClipboard(eventDetail.trace_id, 'Trace ID copiado al portapapeles')"
                  class="ml-2"
                  color="info darken-3"
                  style="cursor: pointer"
                  small
                >
                  mdi-content-copy
                </v-icon>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">Clave de Acceso</div>
              <div class="font-weight-medium text-truncate">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">{{ eventDetail.access_key || '-' }}</span>
                  </template>
                  {{ eventDetail.access_key }}
                </v-tooltip>
                <v-icon
                  v-if="eventDetail.access_key"
                  @click="copyToClipboard(eventDetail.access_key, 'Clave de acceso copiada al portapapeles')"
                  class="ml-2"
                  color="info darken-3"
                  style="cursor: pointer"
                  small
                >
                  mdi-content-copy
                </v-icon>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <div class="text-caption grey--text">Compañía</div>
              <div class="font-weight-medium">{{ eventDetail.company_name || '-' }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="text-caption grey--text">Total Eventos</div>
              <div class="font-weight-medium">{{ eventData.total_attempts || '-' }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="text-caption grey--text">Duración Total</div>
              <div class="font-weight-medium">{{ formatDuration(eventDetail.total_duration_ms) }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <div class="text-caption grey--text">Fecha Inicio</div>
              <div class="font-weight-medium">{{ formatDate(eventDetail.start_time) }}</div>
            </v-col>
          </v-row>
        </v-card-text>
      </base-card>

      
      <base-card>
        <v-card-title class="subtitle-1 font-weight-bold">
          <v-icon left>mdi-timeline</v-icon>
          Timeline de Eventos
        </v-card-title>
        <v-card-text>
          <v-timeline dense align-top>
            <v-timeline-item
              v-for="attempt in eventData.attempts"
              :key="attempt.attempt"
              :color="attempt.status === 'success' ? 'green' : 'red'"
              :icon="attempt.status === 'success' ? 'mdi-check' : 'mdi-close'"
              small
            >
              <v-card outlined class="mb-2">
                <v-card-title class="subtitle-2 pa-3 d-flex align-center">
                  <v-chip
                    :color="attempt.status === 'success' ? 'green' : 'red'"
                    dark
                    x-small
                    label
                    class="mr-2"
                  >
                    Evento {{ attempt.attempt }}
                  </v-chip>
                  <span class="text-caption grey--text mr-2">
                    {{ getProcessLabel(attempt.source_process) }}
                  </span>
                  <span class="text-caption grey--text mr-2">
                    | {{ formatDuration(attempt.duration_ms) }}
                  </span>
                  <span class="text-caption grey--text">
                    | {{ formatDate(attempt.timestamp) }}
                  </span>
                </v-card-title>

                
                <v-card-text class="pa-3 pt-0">
                  <v-stepper vertical non-linear flat class="elevation-0 pa-0">
                    <template v-for="(stage, idx) in attempt.stages">
                      <v-stepper-step
                        :key="'step-' + idx"
                        :step="idx + 1"
                        :complete="stage.status === 'success'"
                        :rules="stage.status === 'error' ? [() => false] : []"
                        error-icon="mdi-close-circle"
                        :color="getStageStatusColor(stage.status)"
                        class="py-1"
                      >
                        <div class="d-flex align-center">
                          <span class="font-weight-medium">{{ getStageLabel(stage.name) }}</span>
                          <v-chip x-small class="ml-2" outlined>
                            {{ formatDuration(stage.duration_ms) }}
                          </v-chip>
                        </div>

                        
                        <v-alert
                          v-if="stage.status === 'error' && stage.details"
                          type="error"
                          dense
                          text
                          class="mt-2 mb-0 text-caption"
                        >
                          <div><strong>Error:</strong> {{ stage.details.error_message }}</div>
                          <div v-if="stage.details.exception_class">
                            <strong>Excepción:</strong> {{ stage.details.exception_class }}
                          </div>
                          <div v-if="stage.details.exception_message">
                            <strong>Detalle:</strong> {{ stage.details.exception_message }}
                          </div>
                        </v-alert>
                      </v-stepper-step>
                    </template>
                  </v-stepper>

                  
                  <v-expansion-panels
                    v-if="attempt.context && Object.keys(attempt.context).length"
                    flat
                    class="mt-3 context-panel"
                  >
                    <v-expansion-panel>
                      <v-expansion-panel-header class="pa-2">
                        <div class="d-flex align-center">
                          <v-icon small class="mr-2">mdi-code-braces</v-icon>
                          <span class="text-caption font-weight-bold">Contexto</span>
                          <v-chip x-small class="ml-2" outlined>
                            {{ Object.keys(attempt.context).length }} campos
                          </v-chip>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-tabs
                          v-model="contextTab[attempt.attempt]"
                          dense
                          height="32"
                          class="context-tabs mb-2"
                        >
                          <v-tab class="text-caption text-capitalize">
                            Todos
                            <v-chip x-small class="ml-1">{{ Object.keys(attempt.context).length }}</v-chip>
                          </v-tab>
                          <v-tab
                            v-if="getContextByCategory(attempt.context, 'error').length"
                            class="text-caption text-capitalize"
                          >
                            Errores
                            <v-chip x-small color="red" dark class="ml-1">{{ getContextByCategory(attempt.context, 'error').length }}</v-chip>
                          </v-tab>
                          <v-tab
                            v-if="getContextByCategory(attempt.context, 'sri').length"
                            class="text-caption text-capitalize"
                          >
                            SRI
                            <v-chip x-small color="blue" dark class="ml-1">{{ getContextByCategory(attempt.context, 'sri').length }}</v-chip>
                          </v-tab>
                          <v-tab
                            v-if="getContextByCategory(attempt.context, 'archivo').length"
                            class="text-caption text-capitalize"
                          >
                            Archivo
                            <v-chip x-small color="teal" dark class="ml-1">{{ getContextByCategory(attempt.context, 'archivo').length }}</v-chip>
                          </v-tab>
                        </v-tabs>

                        <div class="context-scroll">
                          <v-simple-table dense class="context-table">
                            <tbody>
                              <tr
                                v-for="item in getFilteredContext(attempt.context, attempt.attempt)"
                                :key="item.key"
                              >
                                <td class="text-caption font-weight-medium grey--text text--darken-1 context-key">
                                  {{ formatContextKey(item.key) }}
                                </td>
                                <td class="text-caption context-value">
                                  <span
                                    v-if="isLongValue(item.value)"
                                    class="d-inline-block context-long-value"
                                  >
                                    {{ item.value }}
                                  </span>
                                  <template v-else>{{ item.value }}</template>
                                  <v-icon
                                    v-if="isLongValue(item.value)"
                                    x-small
                                    class="ml-1 context-copy-icon"
                                    color="grey"
                                    style="cursor: pointer"
                                    @click="copyToClipboard(String(item.value), formatContextKey(item.key) + ' copiado')"
                                  >
                                    mdi-content-copy
                                  </v-icon>
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>

                  
                  <v-alert
                    v-if="getUniqueAttemptErrors(attempt).length"
                    type="error"
                    dense
                    outlined
                    class="mt-3 mb-0"
                  >
                    <div class="subtitle-2 font-weight-bold mb-1">
                      Errores del evento ({{ getUniqueAttemptErrors(attempt).length }})
                    </div>
                    <div v-for="(err, eIdx) in getUniqueAttemptErrors(attempt)" :key="eIdx" class="text-caption mb-2">
                      <div>
                        <v-icon x-small color="red" class="mr-1">mdi-circle</v-icon>
                        <strong>{{ err.message }}</strong>
                      </div>
                      <div v-if="err.exception_class" class="ml-4">
                        <strong>Excepción:</strong> {{ err.exception_class }}
                      </div>
                      <div v-if="err.exception_message && err.exception_message !== err.message" class="ml-4">
                        <strong>Detalle:</strong> {{ err.exception_message }}
                      </div>
                      <div v-if="err.file" class="ml-4 grey--text text--darken-1">
                        <strong>Archivo:</strong> {{ err.file }}<span v-if="err.line">:{{ err.line }}</span>
                      </div>
                    </div>
                  </v-alert>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
      </base-card>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import {
  formatDuration,
  getStatusColor,
  getStatusIcon,
  getProcessLabel,
  getStageLabel,
  getStageStatusIcon,
} from '@/data/wideEventsStatuses'

export default {
  data () {
    return {
      copyAlert: false,
      copyAlertMessage: '',
      contextTab: {},
    }
  },
  computed: {
    ...mapState('wideEvents', ['eventDetail', 'loadingDetail', 'error']),
    eventData () {
      return (this.eventDetail && this.eventDetail.event) || {}
    },
  },
  created () {
    const traceId = this.$route.params.traceId
    if (traceId) {
      this.$store.dispatch('wideEvents/getEventDetail', traceId)
    }
  },
  methods: {
    formatDuration,
    getStatusColor,
    getStatusIcon,
    getProcessLabel,
    getStageLabel,
    formatDate (dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleString('es-EC', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },
    getStageStatusColor (status) {
      const info = getStageStatusIcon(status)
      return info.color
    },
    formatContextKey (key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    getContextCategory (key) {
      const k = key.toLowerCase()
      if (k.includes('error') || k.includes('exception') || k.includes('has_errors')) return 'error'
      if (k.startsWith('sri_') || k.includes('autorizacion') || k.includes('recepcion')) return 'sri'
      if (k.includes('path') || k.includes('file') || k.includes('output') || k.includes('xml_length') || k.includes('firma_output')) return 'archivo'
      return 'general'
    },
    getContextByCategory (context, category) {
      return Object.entries(context)
        .filter(([key]) => this.getContextCategory(key) === category)
        .map(([key, value]) => ({ key, value }))
    },
    getFilteredContext (context, attemptNum) {
      const tabIndex = this.contextTab[attemptNum] || 0
      const entries = Object.entries(context).map(([key, value]) => ({ key, value }))

      
      const tabs = ['all']
      if (this.getContextByCategory(context, 'error').length) tabs.push('error')
      if (this.getContextByCategory(context, 'sri').length) tabs.push('sri')
      if (this.getContextByCategory(context, 'archivo').length) tabs.push('archivo')

      const selectedTab = tabs[tabIndex] || 'all'
      if (selectedTab === 'all') return entries
      return entries.filter(({ key }) => this.getContextCategory(key) === selectedTab)
    },
    isLongValue (value) {
      return String(value).length > 60
    },
    getStageStatusIconName (status) {
      const info = getStageStatusIcon(status)
      return info.icon
    },
    getUniqueAttemptErrors (attempt) {
      const errors = (attempt && attempt.errors) || []
      if (!errors.length) return []
      const stageSignatures = new Set()
      const stages = (attempt && attempt.stages) || []
      stages.forEach(stage => {
        const d = stage && stage.details
        if (!d) return
        const sig = [d.error_message || '', d.exception_class || '', d.exception_message || ''].join('||')
        if (sig !== '||||') stageSignatures.add(sig)
      })
      return errors.filter(err => {
        const sig = [err.message || '', err.exception_class || '', err.exception_message || ''].join('||')
        return !stageSignatures.has(sig)
      })
    },
    async copyToClipboard (text, message) {
      try {
        await navigator.clipboard.writeText(text)
        this.copyAlertMessage = message
        this.copyAlert = true
        setTimeout(() => {
          this.copyAlert = false
        }, 3000)
      } catch (e) {
        console.error('No se pudo copiar al portapapeles', e)
      }
    },
  },
}
</script>

<style scoped>
::v-deep .v-stepper__step--error .v-stepper__step__step .v-icon {
  font-size: 26px;
}

.context-panel {
  border: thin solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.context-panel ::v-deep .v-expansion-panel::before {
  box-shadow: none;
}
.context-panel ::v-deep .v-expansion-panel-header {
  min-height: 36px;
}
.context-panel ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 8px 8px;
}

.context-tabs ::v-deep .v-tab {
  min-width: unset;
  padding: 0 8px;
  letter-spacing: normal;
}
.context-tabs ::v-deep .v-tabs-bar {
  height: 32px;
}

.context-scroll {
  max-height: 260px;
  overflow-y: auto;
  border: thin solid rgba(0, 0, 0, 0.06);
  border-radius: 4px;
}

.context-table {
  background: transparent !important;
}
.context-table td {
  padding: 4px 10px !important;
  height: auto !important;
  border-bottom: thin solid rgba(0, 0, 0, 0.06) !important;
}
.context-key {
  width: 180px;
  white-space: nowrap;
}
.context-value {
  word-break: break-all;
}
.context-long-value {
  max-width: 400px;
  word-break: break-all;
}
.context-copy-icon {
  vertical-align: middle;
  opacity: 0.5;
}
.context-copy-icon:hover {
  opacity: 1;
}
</style>

